/**
 * CustomerSignInModal.jsx
 *
 * Sign-in modal for marketplace customers.
 * Primary: standard email + password. Secondary: Google SSO, phone OTP.
 * First-time / forgot password → emailed set-password link.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getMarketplaceCountry } from '../utils/marketplaceLocale';
import PhoneInput from './PhoneInput';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, Building2, Smartphone, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Google logo SVG
const GoogleLogo = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const errText = (detail, fallback) => {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
};

const CustomerSignInModal = ({ open, onOpenChange, onSuccess }) => {
  const { loginWithGoogle, refreshAuth } = useCustomerAuth();
  const [view, setView] = useState('main'); // 'main' | 'reset' | 'reset-sent' | 'phone' | 'phone-code'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [resetContext, setResetContext] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(getMarketplaceCountry() === 'UAE' ? 'AE' : 'US');
  const [phone, setPhone] = useState(''); // E.164 from PhoneInput
  const [channelChoice, setChannelChoice] = useState(getMarketplaceCountry() === 'UAE' ? 'whatsapp' : 'sms');
  const [codeChannel, setCodeChannel] = useState('sms');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Country change nudges the sensible default channel (user can still override)
  React.useEffect(() => {
    setChannelChoice(phoneCountry === 'AE' ? 'whatsapp' : 'sms');
  }, [phoneCountry]);

  const fullPhone = phone;

  const handleGoogleSignIn = () => {
    sessionStorage.setItem('customerAuthRedirect', window.location.pathname);
    loginWithGoogle();
  };

  const finishSignIn = async (customerName) => {
    localStorage.setItem('bf_customer_session', '1');
    toast.success(`Welcome back${customerName ? `, ${customerName.split(' ')[0]}` : ''}!`);
    await refreshAuth?.();
    onOpenChange(false);
    resetModal();
    onSuccess?.();
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!email.includes('@')) { setLoginError('Enter a valid email address'); return; }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok) {
        await finishSignIn(data.customer?.name);
      } else if (data.detail && typeof data.detail === 'object' && data.detail.code === 'password_setup_required') {
        setResetContext(data.detail.message || 'First time logging in — set your password to continue.');
        setView('reset');
      } else {
        setLoginError(errText(data.detail, 'Sign-in failed. Please try again.'));
      }
    } catch {
      setLoginError('Sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/password/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setView('reset-sent');
      } else {
        const data = await response.json();
        toast.error(errText(data.detail, 'Failed to send link'));
      }
    } catch {
      toast.error('Failed to send link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneStart = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/phone/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: fullPhone, channel: channelChoice }),
      });
      const data = await response.json();
      if (response.ok) {
        setCodeChannel(data.channel || channelChoice);
        setView('phone-code');
        toast.success(data.channel === 'whatsapp' ? 'Code sent — check WhatsApp' : 'Code sent — check your texts');
      } else {
        toast.error(errText(data.detail, 'Failed to send code'));
      }
    } catch {
      toast.error('Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: fullPhone, code }),
      });
      const data = await response.json();
      if (response.ok) {
        await finishSignIn(data.customer?.name);
      } else {
        toast.error(errText(data.detail, 'Invalid code'));
      }
    } catch {
      toast.error('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setView('main');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setLoginError('');
    setResetContext('');
    setPhone('');
    setCode('');
    setIsLoading(false);
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen) resetModal();
    onOpenChange(newOpen);
  };

  const titles = {
    main: 'Sign in',
    reset: 'Set your password',
    'reset-sent': 'Check your email',
    phone: 'Sign in with phone',
    'phone-code': 'Enter your code',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {titles[view] || 'Sign in'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Main View — Email + Password */}
          {view === 'main' && (
            <>
              <div className="mb-5">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">
                  Customer
                </p>

                <form onSubmit={handlePasswordLogin} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12"
                    autoComplete="email"
                    required
                    data-testid="customer-email-input"
                  />
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 pr-11"
                      autoComplete="current-password"
                      required
                      data-testid="customer-password-input"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {loginError && (
                    <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2" data-testid="customer-login-error">{loginError}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
                    disabled={isLoading}
                    data-testid="customer-signin-submit"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
                  </Button>
                </form>

                <button
                  type="button"
                  onClick={() => { setResetContext(''); setView('reset'); }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium mt-3"
                  data-testid="forgot-password-link"
                >
                  First time logging in? / Forgot password?
                </button>
              </div>

              {/* Divider */}
              <div className="relative my-5">
                <div className="border-t border-gray-200" />
                <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-white px-3 text-xs text-gray-400">or continue with</span>
              </div>

              <div className="flex gap-3 mb-5">
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-full text-sm font-medium border-0 bg-gray-50 shadow-none hover:bg-gray-100"
                  onClick={handleGoogleSignIn}
                  data-testid="google-signin-btn"
                >
                  <GoogleLogo />
                  Google
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-full text-sm font-medium border-0 bg-gray-50 shadow-none hover:bg-gray-100"
                  onClick={() => setView('phone')}
                  data-testid="phone-signin-btn"
                >
                  <Smartphone className="w-5 h-5 mr-2 text-gray-600" />
                  Phone
                </Button>
              </div>

              {/* Operator / Staff section */}
              <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">
                  Operator, Owner, or Staff
                </p>
                <Link
                  to="/login"
                  onClick={() => handleOpenChange(false)}
                  className="flex items-center justify-center w-full h-12 px-4 rounded-full bg-white hover:bg-blue-50 transition-colors text-base font-medium text-gray-800 gap-3"
                  data-testid="operator-signin-btn"
                >
                  <Building2 className="w-5 h-5 text-gray-600" />
                  Sign in to dashboard
                </Link>
              </div>

              <p className="text-center text-xs text-gray-400">
                By continuing, you agree to our{' '}
                <a href="/terms" className="underline hover:text-gray-600">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a>
              </p>
            </>
          )}

          {/* Reset / First-time password setup */}
          {view === 'reset' && (
            <>
              <button
                onClick={() => setView('main')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <p className="text-center text-gray-600 mb-6 text-sm">
                {resetContext || 'Enter the email you booked with and we\u2019ll send you a secure link to set your password.'}
              </p>

              <form onSubmit={handleSendResetLink} className="space-y-4">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                  data-testid="reset-email-input"
                />
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
                  disabled={isLoading}
                  data-testid="send-reset-link-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" />Email me a set-password link</>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Reset link sent */}
          {view === 'reset-sent' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-gray-600 mb-2">If an account exists for</p>
              <p className="font-medium text-gray-900 mb-4">{email}</p>
              <p className="text-sm text-gray-500 mb-6">
                you&apos;ll receive a link to set your password. It expires in 30 minutes.
              </p>
              <Button variant="outline" className="w-full rounded-full" onClick={() => setView('reset')}>
                Didn&apos;t receive it? Try again
              </Button>
            </div>
          )}

          {/* Phone Sign-In */}
          {view === 'phone' && (
            <>
              <button
                onClick={() => setView('main')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <p className="text-center text-gray-600 mb-6 text-sm">
                We&apos;ll send a one-time code to the phone number on your account — the one you booked with
              </p>

              <form onSubmit={handlePhoneStart} className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone number
                  </label>
                  <PhoneInput
                    id="phone"
                    value={phone}
                    defaultCountry={phoneCountry}
                    onChange={(e164, meta) => { setPhone(e164); if (meta?.country) setPhoneCountry(meta.country); }}
                    data-testid="phone-signin-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Send my code by</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setChannelChoice('whatsapp')}
                      data-testid="channel-whatsapp-btn"
                      className={`h-11 rounded-full border text-sm font-medium transition-colors ${channelChoice === 'whatsapp' ? 'border-transparent bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500' : 'border-transparent ring-1 ring-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => setChannelChoice('sms')}
                      data-testid="channel-sms-btn"
                      className={`h-11 rounded-full border text-sm font-medium transition-colors ${channelChoice === 'sms' ? 'border-transparent bg-blue-50 text-blue-700 ring-1 ring-blue-500' : 'border-transparent ring-1 ring-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      Text (SMS)
                    </button>
                  </div>
                  {channelChoice === 'sms' && phone.startsWith('+971') && (
                    <p className="text-xs text-amber-600 mt-1.5" data-testid="uae-sms-warning">
                      Heads up — SMS delivery in the UAE is unreliable right now. WhatsApp is recommended.
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading || phone.replace(/\D/g, '').length < 8}
                  data-testid="send-phone-code-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Smartphone className="w-4 h-4 mr-2" />{channelChoice === 'whatsapp' ? 'Send code on WhatsApp' : 'Text me a code'}</>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Phone Code Entry */}
          {view === 'phone-code' && (
            <>
              <button
                onClick={() => setView('phone')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <p className="text-center text-gray-600 mb-2">
                Enter the code we sent {codeChannel === 'whatsapp' ? 'on WhatsApp' : 'by text'} to
              </p>
              <p className="text-center font-medium text-gray-900 mb-6">{fullPhone}</p>

              <form onSubmit={handlePhoneVerify} className="space-y-4">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-12 text-center text-xl tracking-[0.4em] font-semibold"
                  maxLength={8}
                  required
                  data-testid="phone-code-input"
                />
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                  data-testid="verify-phone-code-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Verify & sign in'}
                </Button>
                <button
                  type="button"
                  onClick={handlePhoneStart}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Didn&apos;t get it? Resend code
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerSignInModal;
